/**
 * useMessageSound — synthetic, royalty-free messaging tones.
 *
 * Three DISTINCT tones, all generated on the fly via WebAudio so we
 * never ship copyrighted audio assets:
 *
 *   • "sent"     — ~80 ms subtle high-pitched "tick" (1800 Hz → quick
 *                  exponential decay). Plays ONLY after a successful
 *                  POST /api/messages.
 *   • "received" — two-note rising chime (660 Hz → 990 Hz, ~180 ms
 *                  total). Distinct from the sent tick and from the
 *                  global notification tone.
 *   • "read"     — very subtle descending blip (1100 Hz → 800 Hz,
 *                  ~120 ms). Only plays for the sender when their own
 *                  message is marked read.
 *
 * Constraints honoured:
 *   • Respects the user's mute preference (localStorage key
 *     `ccuk_msg_sound_enabled`, mirrored from
 *     `messenger_preferences.sound_enabled` in Account Settings).
 *   • Respects browser autoplay rules: the AudioContext is created on
 *     the first user gesture and resumed thereafter. We never call
 *     `play()` before the user has interacted with the page.
 *   • De-duplication: throttles bursts to one play per variant every
 *     600 ms so a stream of incoming messages doesn't sound like
 *     popcorn.
 */
import { useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'ccuk_msg_sound_enabled';
// Notification tone — one of: classic | chime | pop | soft | off.
// Mirror of `messenger_preferences.notification_tone`.
const TONE_KEY = 'ccuk_msg_sound_tone';
// Quiet hours — when ON, message sounds (sent / received / read) are
// suppressed between start and end (user-local HH:MM). The visible
// notification toast / unread counter / badge are unaffected.
const QUIET_ENABLED_KEY = 'ccuk_msg_quiet_enabled';
const QUIET_START_KEY = 'ccuk_msg_quiet_start';
const QUIET_END_KEY = 'ccuk_msg_quiet_end';

// Throttle bursts per variant
const _lastPlay = { sent: 0, received: 0, read: 0 };

/**
 * Internal: lazily-instantiated, shared AudioContext. Most browsers
 * cap the number of contexts a page may create, so we keep exactly
 * one and `resume()` it on each user gesture.
 */
let _audioCtx = null;
function _getCtx() {
  if (typeof window === 'undefined') return null;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!_audioCtx) {
    try { _audioCtx = new AudioCtx(); } catch { return null; }
  }
  return _audioCtx;
}

/**
 * Play a single sine-wave envelope. Used as the building block for
 * every tone — keeps the code tiny while letting each tone have its
 * own pitch curve + duration + peak gain.
 */
function _ping(ctx, { startHz, endHz = null, duration = 0.1, peak = 0.12, delay = 0 }) {
  try {
    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(startHz, t0);
    if (endHz && endHz !== startHz) {
      osc.frequency.exponentialRampToValueAtTime(endHz, t0 + duration);
    }
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  } catch { /* ignore */ }
}

function _playSentClassic(ctx) {
  // Subtle high tick, ~80 ms, low volume. Distinct from notification
  // and from received — it should feel like a confirmation, not an
  // alert.
  _ping(ctx, { startHz: 1800, endHz: 1200, duration: 0.08, peak: 0.06 });
}

function _playReceivedClassic(ctx) {
  // Two-note rising chime so a new message feels welcoming rather
  // than alarming.
  _ping(ctx, { startHz: 784, endHz: 988, duration: 0.12, peak: 0.085, delay: 0 });
  _ping(ctx, { startHz: 1175, endHz: 1568, duration: 0.22, peak: 0.065, delay: 0.08 });
}

function _playReadClassic(ctx) {
  // Very subtle descending blip — only fires for our own sent
  // messages becoming "Read", so it should be barely audible.
  _ping(ctx, { startHz: 1100, endHz: 800, duration: 0.12, peak: 0.05 });
}

// ─── Alternative tones ─────────────────────────────────────────────
// Each tone re-uses the same _ping primitive. The shape lookup below
// keeps `play(variant)` dispatch identical regardless of the user's
// selected tone — the variant→player mapping is resolved at play time
// from `_getTone()`.

function _playSentChime(ctx)   { _ping(ctx, { startHz: 1500, endHz: 1500, duration: 0.06, peak: 0.05 }); }
function _playRecvChime(ctx)   { _ping(ctx, { startHz: 880, endHz: 1320, duration: 0.20, peak: 0.13 }); }
function _playReadChime(ctx)   { _ping(ctx, { startHz: 1100, endHz: 880,  duration: 0.10, peak: 0.04 }); }

function _playSentPop(ctx)     { _ping(ctx, { startHz: 220, endHz: 110, duration: 0.05, peak: 0.10 }); }
function _playRecvPop(ctx)     { _ping(ctx, { startHz: 330, endHz: 165, duration: 0.07, peak: 0.16 }); }
function _playReadPop(ctx)     { _ping(ctx, { startHz: 200, endHz: 100, duration: 0.05, peak: 0.05 }); }

function _playSentSoft(ctx)    { _ping(ctx, { startHz: 540, endHz: 540, duration: 0.08, peak: 0.05 }); }
function _playRecvSoft(ctx)    {
  _ping(ctx, { startHz: 392, endHz: 392, duration: 0.18, peak: 0.10 });
  _ping(ctx, { startHz: 587, endHz: 587, duration: 0.22, peak: 0.10, delay: 0.06 });
}
function _playReadSoft(ctx)    { _ping(ctx, { startHz: 660, endHz: 540, duration: 0.14, peak: 0.04 }); }

const TONE_PLAYERS = {
  classic: { sent: _playSentClassic, received: _playReceivedClassic, read: _playReadClassic },
  chime:   { sent: _playSentChime,   received: _playRecvChime,       read: _playReadChime   },
  pop:     { sent: _playSentPop,     received: _playRecvPop,         read: _playReadPop     },
  soft:    { sent: _playSentSoft,    received: _playRecvSoft,        read: _playReadSoft    },
  off:     { sent: null, received: null, read: null },
};

/** Read the current tone selection (defaults to 'classic'). */
function _getTone() {
  try {
    const v = window.localStorage.getItem(TONE_KEY);
    if (v && TONE_PLAYERS[v]) return v;
  } catch { /* default */ }
  return 'classic';
}

/**
 * Quiet-hours check — true when the user has enabled quiet hours AND
 * the current local time falls inside [start, end). Handles wrap-around
 * (e.g. 22:00 → 08:00) correctly.
 */
function _inQuietHours() {
  try {
    if (window.localStorage.getItem(QUIET_ENABLED_KEY) !== '1') return false;
    const s = (window.localStorage.getItem(QUIET_START_KEY) || '22:00').match(/^(\d{1,2}):(\d{2})$/);
    const e = (window.localStorage.getItem(QUIET_END_KEY)   || '08:00').match(/^(\d{1,2}):(\d{2})$/);
    if (!s || !e) return false;
    const sMin = Number(s[1]) * 60 + Number(s[2]);
    const eMin = Number(e[1]) * 60 + Number(e[2]);
    if (sMin === eMin) return false; // zero-length window — treat as off
    const now = new Date();
    const nMin = now.getHours() * 60 + now.getMinutes();
    return sMin < eMin
      ? (nMin >= sMin && nMin < eMin)
      : (nMin >= sMin || nMin < eMin); // wrap-around
  } catch { return false; }
}

export default function useMessageSound() {
  const unlockedRef = useRef(false);

  useEffect(() => {
    // Unlock the AudioContext on the first real user gesture. Most
    // browsers require this before any oscillator can be heard.
    const unlock = () => {
      unlockedRef.current = true;
      const ctx = _getCtx();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => { /* ignore */ });
      }
      window.removeEventListener('click', unlock, true);
      window.removeEventListener('keydown', unlock, true);
      window.removeEventListener('touchstart', unlock, true);
    };
    window.addEventListener('click', unlock, true);
    window.addEventListener('keydown', unlock, true);
    window.addEventListener('touchstart', unlock, true);
    return () => {
      window.removeEventListener('click', unlock, true);
      window.removeEventListener('keydown', unlock, true);
      window.removeEventListener('touchstart', unlock, true);
    };
  }, []);

  const play = useCallback((variant) => {
    if (!unlockedRef.current) return;
    // Read the live setting at play-time so toggling the preference
    // takes effect immediately without rerendering the hook.
    let enabled = true;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      enabled = raw === null ? true : raw === '1';
    } catch { /* default ON */ }
    if (!enabled) return;

    // Suppress sound during user-defined quiet hours. Visible UI
    // (toast / badge / unread count) is intentionally unaffected —
    // we mute audio only.
    if (_inQuietHours()) return;

    // Burst throttle — max one play per variant per 600 ms.
    const now = Date.now();
    if (_lastPlay[variant] && now - _lastPlay[variant] < 600) return;
    _lastPlay[variant] = now;

    const ctx = _getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      // Try once more — some browsers suspend after a few minutes idle.
      ctx.resume().catch(() => { /* ignore */ });
    }
    const tone = _getTone();
    const player = (TONE_PLAYERS[tone] || TONE_PLAYERS.classic)[variant];
    if (player) player(ctx);
  }, []);

  return play;
}

/**
 * Mute helper — read/write the local mute flag. The backend
 * `messenger_preferences.sound_enabled` is the canonical source; the
 * AccountSettings UI mirrors it into localStorage so cross-tab plays
 * respect the toggle without re-fetching prefs on every play.
 */
export function setSoundEnabled(enabled) {
  try { window.localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0'); } catch { /* ignore */ }
}

export function isSoundEnabled() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === '1';
  } catch { return true; }
}

// ─── Tone + Quiet hours (mirrored from backend prefs) ──────────────

/** Persist selected tone (one of: classic | chime | pop | soft | off). */
export function setSoundTone(tone) {
  if (!TONE_PLAYERS[tone]) return;
  try { window.localStorage.setItem(TONE_KEY, tone); } catch { /* ignore */ }
}

export function getSoundTone() { return _getTone(); }

/** Persist quiet-hours config (enabled flag + HH:MM start/end). */
export function setQuietHours({ enabled, start, end } = {}) {
  try {
    if (typeof enabled === 'boolean') {
      window.localStorage.setItem(QUIET_ENABLED_KEY, enabled ? '1' : '0');
    }
    if (typeof start === 'string') window.localStorage.setItem(QUIET_START_KEY, start);
    if (typeof end === 'string')   window.localStorage.setItem(QUIET_END_KEY, end);
  } catch { /* ignore */ }
}

/** Read quiet-hours config — used by AccountSettings to hydrate inputs. */
export function getQuietHours() {
  try {
    return {
      enabled: window.localStorage.getItem(QUIET_ENABLED_KEY) === '1',
      start: window.localStorage.getItem(QUIET_START_KEY) || '22:00',
      end: window.localStorage.getItem(QUIET_END_KEY) || '08:00',
    };
  } catch {
    return { enabled: false, start: '22:00', end: '08:00' };
  }
}

/** Test-only — preview-play a tone variant. Used by Settings UI. */
export function previewTone(tone, variant = 'received') {
  const ctx = _getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const player = (TONE_PLAYERS[tone] || TONE_PLAYERS.classic)[variant];
  if (player) player(ctx);
}
